import React, { useEffect, useState } from 'react';
import { Gift, X } from 'lucide-react';
import { getStoredPromo } from '../../lib/promoLink';
import { isNativeApp } from '../../lib/platform';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Surfaces a captured promo code with a friendly auto-applied banner.
 * Validates the code against the backend so we only surface live promos.
 * Dismissible per session (resets if user refreshes).
 */
export default function PromoBanner({ className = '' }) {
  const [code, setCode] = useState(null);
  const [summary, setSummary] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stored = getStoredPromo();
    if (!stored) return;
    fetch(`${API}/api/auth/validate-discount`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: stored }),
    })
      .then(async (r) => {
        if (!r.ok) return null;
        return r.json();
      })
      .then((d) => {
        if (!d || !d.code) return;
        const label = d.type === 'extended_trial'
          ? `${d.trial_days}-day free trial`
          : `${d.percent_off}% off ${d.type === 'first_month' ? 'first month' : d.type === 'first_year' ? 'first year' : 'every renewal'}`;
        setCode(d.code);
        setSummary(label);
      })
      .catch(() => {});
  }, []);

  if (isNativeApp() || !code || dismissed) return null;

  return (
    <div
      className={`relative bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-300 text-stone-900 rounded-2xl px-4 py-3 shadow-lg border border-amber-500/40 ${className}`}
      data-testid="promo-banner"
    >
      <div className="flex items-center gap-3 pr-7">
        <div className="w-9 h-9 rounded-full bg-stone-900 text-amber-300 flex items-center justify-center shrink-0">
          <Gift size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-bold text-stone-700 leading-tight">
            Promo unlocked
          </p>
          <p className="text-sm font-bold leading-tight">
            Code <span className="font-mono">{code}</span> — {summary}. Auto-applied at signup.
          </p>
        </div>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-stone-900/10 flex items-center justify-center"
        aria-label="Dismiss promo banner"
        data-testid="promo-banner-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
